import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  Shield,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  Filter,
  Map,
  BarChart3,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  approveMap,
  rejectMap,
  getAllMapsForApprover,
} from "../../services/adminService";
import { MapData } from "../../types";

// Styled Components
const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  background: var(--background-color);
  color: var(--text-color);
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  background: var(--surface-color);
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-color);
  border: 1px solid var(--border-color);

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  overflow-x: auto;
  padding-bottom: 0.5rem;

  @media (min-width: 768px) {
    overflow-x: visible;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${(props) =>
    props.$active ? "var(--primary-color)" : "transparent"};
  color: ${(props) =>
    props.$active ? "white" : "var(--text-secondary-color)"};
  border: none;
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-size: 0.9rem;

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--primary-color)" : "var(--background-color)"};
    opacity: ${(props) => (props.$active ? "0.9" : "0.8")};
  }
`;

const TabContent = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 8px var(--shadow-color);
  border: 1px solid var(--border-color);

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const TableContainer = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--shadow-color);
  border: 1px solid var(--border-color);
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-color);

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    max-width: 600px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.875rem;
  background: var(--surface-color);
  color: var(--text-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-color);
    opacity: 0.1;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.875rem;
  background: var(--surface-color);
  color: var(--text-color);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-color);
    opacity: 0.1;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const TableHead = styled.thead`
  background: var(--background-color);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border-color);

  &:hover {
    background: var(--background-color);
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-secondary-color);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (min-width: 768px) {
    padding: 1rem;
    font-size: 0.875rem;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem;
  vertical-align: top;
  font-size: 12px;
  color: var(--text-color);

  @media (min-width: 768px) {
    padding: 1rem;
    font-size: 14px;
  }
`;

const MapTitle = styled.div`
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 0.25rem;
  font-size: 12px;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const MapAuthor = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary-color);

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const MapStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.7rem;
  color: var(--text-secondary-color);

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`;

const StatusBadge = styled.span<{
  $status: "pending" | "approved" | "rejected" | "public";
}>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  background: ${(props) => {
    switch (props.$status) {
      case "pending":
        return "var(--warning-bg)";
      case "approved":
        return "var(--info-bg)";
      case "rejected":
        return "var(--error-bg)";
      case "public":
        return "var(--success-bg)";
      default:
        return "var(--private-bg)";
    }
  }};

  color: ${(props) => {
    switch (props.$status) {
      case "pending":
        return "var(--warning-text)";
      case "approved":
        return "var(--info-text)";
      case "rejected":
        return "var(--error-text)";
      case "public":
        return "var(--success-text)";
      default:
        return "var(--private-text)";
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    gap: 0.5rem;
  }
`;

const ActionButton = styled.button<{
  $variant: "success" | "danger" | "warning";
}>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.6rem;
  border: none;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }

  background: ${(props) => {
    switch (props.$variant) {
      case "success":
        return "var(--success-color)";
      case "danger":
        return "var(--error-color)";
      case "warning":
        return "var(--warning-button-bg, var(--warning-color))";
      default:
        return "var(--secondary-color)";
    }
  }};

  color: ${(props) =>
    props.$variant === "warning"
      ? "var(--warning-button-text, var(--warning-text))"
      : "white"};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-color);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary-color);

  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

// Types
type TabType = "overview" | "maps";
type StatusFilter = "all" | "pending" | "approved" | "rejected" | "public";

const ApproverDashboard: React.FC = () => {
  const { user, isApprover } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [allMaps, setAllMaps] = useState<MapData[]>([]);
  const [filteredMaps, setFilteredMaps] = useState<MapData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [regionFilter] = useState<string>("iran"); // Default to Iran for now
  const [stats, setStats] = useState({
    totalMaps: 0,
    pendingApprovals: 0,
    approvedMaps: 0,
    publicMaps: 0,
  });

  const loadApproverData = useCallback(async () => {
    try {
      const maps = await getAllMapsForApprover();

      // Filter maps based on region (for now, just Iran)
      const regionMaps = maps.filter(
        (map: MapData) =>
          !map.tags || map.tags.includes(regionFilter) || map.tags.length === 0
      );

      setAllMaps(regionMaps);

      // Calculate stats
      const pendingCount = regionMaps.filter(
        (m: MapData) => m.approvalRequested && !m.isRejected && !m.isApproved
      ).length;
      const approvedCount = regionMaps.filter(
        (m: MapData) => m.isApproved && !m.isPublic
      ).length;
      const publicCount = regionMaps.filter((m: MapData) => m.isPublic).length;

      setStats({
        totalMaps: regionMaps.length,
        pendingApprovals: pendingCount,
        approvedMaps: approvedCount,
        publicMaps: publicCount,
      });
    } catch (error) {
      console.error("Failed to load approver data:", error);
      toast.error("Failed to load data");
    }
  }, [regionFilter]);

  useEffect(() => {
    if (user && isApprover()) {
      loadApproverData();
    }
  }, [user, regionFilter, isApprover, loadApproverData]);

  // Filter maps based on search term and status filter
  useEffect(() => {
    let filtered = allMaps;

    // Filter by status (exclude private maps)
    if (statusFilter !== "all") {
      filtered = filtered.filter((map) => {
        switch (statusFilter) {
          case "pending":
            return map.approvalRequested && !map.isRejected && !map.isApproved;
          case "approved":
            return map.isApproved && !map.isPublic;
          case "rejected":
            return map.isRejected;
          case "public":
            return map.isPublic;
          default:
            return true;
        }
      });
    } else {
      // For "all" filter, exclude private maps
      filtered = filtered.filter(
        (map) =>
          map.approvalRequested ||
          map.isApproved ||
          map.isRejected ||
          map.isPublic
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (map) =>
          map.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          map.userDisplayName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          map.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredMaps(filtered);
  }, [allMaps, searchTerm, statusFilter]);

  if (!user || !isApprover()) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access the approver dashboard.</p>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Shield size={32} color="#28a745" />
        <Title>
          Approver Dashboard -{" "}
          {regionFilter.charAt(0).toUpperCase() + regionFilter.slice(1)}
        </Title>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatTitle>Total Maps</StatTitle>
          <StatValue>{stats.totalMaps}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Pending Approvals</StatTitle>
          <StatValue>{stats.pendingApprovals}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Approved Maps</StatTitle>
          <StatValue>{stats.approvedMaps}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Public Maps</StatTitle>
          <StatValue>{stats.publicMaps}</StatValue>
        </StatCard>
      </StatsGrid>

      <TabsContainer>
        <Tab
          $active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
        >
          <BarChart3 size={16} />
          Overview
        </Tab>
        <Tab
          $active={activeTab === "maps"}
          onClick={() => setActiveTab("maps")}
        >
          <Map size={16} />
          Maps
        </Tab>
      </TabsContainer>

      <TabContent>
        {activeTab === "overview" && (
          <div>
            <h3>Approval Overview</h3>
            <p>
              Welcome to the approver dashboard for{" "}
              {regionFilter.charAt(0).toUpperCase() + regionFilter.slice(1)}.
              Here you can review and approve maps that have been submitted for
              approval.
            </p>
            <div style={{ marginTop: "2rem" }}>
              <h4>Quick Actions</h4>
              <ul>
                <li>Review pending map approvals</li>
                <li>Approve or reject submitted maps</li>
                <li>Monitor approved maps in your region</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === "maps" && (
          <div>
            <h3>Map Approval Management</h3>

            <TableContainer>
              <TableHeader>
                <SearchContainer>
                  <Search size={16} color="#666" />
                  <SearchInput
                    type="text"
                    placeholder="Search maps by name, author, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Filter size={16} color="#666" />
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value as StatusFilter)
                    }
                  >
                    <option value="all">All Maps (No Private)</option>
                    <option value="pending">Pending Approval</option>
                    <option value="approved">Approved (Private)</option>
                    <option value="public">Public</option>
                    <option value="rejected">Rejected</option>
                  </FilterSelect>
                </SearchContainer>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {filteredMaps.length} map
                  {filteredMaps.length !== 1 ? "s" : ""} found
                </div>
              </TableHeader>

              {filteredMaps.length === 0 ? (
                <EmptyState>
                  <EmptyStateIcon>🗺️</EmptyStateIcon>
                  <h4>No maps found</h4>
                  <p>Try adjusting your search or filter criteria.</p>
                </EmptyState>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Map</TableHeaderCell>
                      <TableHeaderCell>Author</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                      <TableHeaderCell>Stats</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {filteredMaps.map((map) => {
                      // Determine map status
                      let status:
                        | "pending"
                        | "approved"
                        | "rejected"
                        | "public" = "pending";
                      if (map.isPublic) {
                        status = "public";
                      } else if (map.isRejected) {
                        status = "rejected";
                      } else if (map.isApproved) {
                        status = "approved";
                      } else if (map.approvalRequested) {
                        status = "pending";
                      }

                      return (
                        <TableRow key={map.id}>
                          <TableCell>
                            <MapTitle>{map.name}</MapTitle>
                            {map.description && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  marginTop: "4px",
                                }}
                              >
                                {map.description.length > 50
                                  ? `${map.description.substring(0, 50)}...`
                                  : map.description}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <MapAuthor>{map.userDisplayName}</MapAuthor>
                          </TableCell>
                          <TableCell>
                            <StatusBadge $status={status}>
                              {status === "pending" && "⏳ Pending"}
                              {status === "approved" && "✅ Approved"}
                              {status === "rejected" && "❌ Rejected"}
                              {status === "public" && "🌐 Public"}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            {new Date(map.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <MapStats>
                              <span>States: {map.states.length}</span>
                              <span>Likes: {map.likesCount}</span>
                            </MapStats>
                          </TableCell>
                          <TableCell>
                            <ActionButtons>
                              {/* Show approve button only for pending maps */}
                              {status === "pending" && (
                                <ActionButton
                                  $variant="success"
                                  onClick={async () => {
                                    try {
                                      await approveMap(map.id);
                                      toast.success(
                                        "Map approved successfully"
                                      );
                                      loadApproverData(); // Reload data
                                    } catch (error) {
                                      toast.error("Failed to approve map");
                                    }
                                  }}
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </ActionButton>
                              )}

                              {/* Show reject button for pending and approved maps */}
                              {(status === "pending" ||
                                status === "approved") && (
                                <ActionButton
                                  $variant="danger"
                                  onClick={async () => {
                                    const reason = prompt(
                                      "Please provide a reason for rejection (optional):"
                                    );
                                    try {
                                      await rejectMap(
                                        map.id,
                                        reason || undefined
                                      );
                                      toast.success(
                                        "Map rejected successfully"
                                      );
                                      loadApproverData(); // Reload data
                                    } catch (error) {
                                      toast.error("Failed to reject map");
                                    }
                                  }}
                                >
                                  <XCircle size={14} />
                                  Reject
                                </ActionButton>
                              )}

                              {/* Show view button for all maps */}
                              <ActionButton
                                $variant="warning"
                                onClick={() =>
                                  window.open(`/map/${map.id}`, "_blank")
                                }
                              >
                                <Eye size={14} />
                                View
                              </ActionButton>
                            </ActionButtons>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </TableContainer>
          </div>
        )}
      </TabContent>
    </DashboardContainer>
  );
};

export default ApproverDashboard;
